import { MapPin, Clock, Phone, Mail, Camera } from "lucide-react";
import { MotifDivider } from "../components/Motif";
import { useContent } from "../lib/content";

export default function Visit() {
  const { content: c } = useContent();

  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${c.map_lat},${c.map_lng}`;
  const embedUrl = `https://maps.google.com/maps?q=${c.map_lat},${c.map_lng}&z=16&output=embed`;
  const gallery = [c.gallery_image_1, c.gallery_image_2, c.gallery_image_3, c.gallery_image_4].filter(Boolean);

  return (
    <div className="fade-up" data-testid="visit-page">
      <section className="px-6 md:px-12 lg:px-20 pt-16 md:pt-20 pb-10">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <span className="eyebrow">{c.visit_page_eyebrow}</span>
            <h1 className="font-serif-display text-5xl md:text-6xl mt-3 leading-tight">
              {c.visit_page_title_part1}{" "}
              <em className="not-italic text-[var(--srishti-saffron)]">{c.visit_page_title_part2}</em>.
            </h1>
            <p className="mt-6 text-[var(--srishti-text-secondary)] leading-relaxed">{c.visit_page_subtitle}</p>

            <ul className="mt-10 space-y-5 text-sm">
              <li className="flex gap-4">
                <MapPin className="text-[var(--srishti-saffron)] mt-0.5 shrink-0" size={18} />
                <span>{c.store_address}</span>
              </li>
              <li className="flex gap-4">
                <Clock className="text-[var(--srishti-saffron)] mt-0.5 shrink-0" size={18} />
                <span>{c.store_hours}</span>
              </li>
              <li className="flex gap-4">
                <Phone className="text-[var(--srishti-saffron)] mt-0.5 shrink-0" size={18} />
                <span>{c.store_phone}</span>
              </li>
              <li className="flex gap-4">
                <Mail className="text-[var(--srishti-saffron)] mt-0.5 shrink-0" size={18} />
                <span>{c.store_email}</span>
              </li>
            </ul>

            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-maroon mt-10 inline-flex"
              data-testid="open-maps-button"
            >
              {c.visit_page_cta}
            </a>
          </div>

          <div className="aspect-[4/5] image-zoom bg-[var(--srishti-bg-alt)] overflow-hidden">
            <img
              src={c.visit_image}
              alt="Boutique"
              className="w-full h-full object-cover"
              style={{ objectPosition: "center 60%" }}
              data-testid="visit-image"
            />
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-16 bg-[var(--srishti-bg-alt)]" data-testid="gallery-section">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-10">
            <span className="eyebrow">{c.gallery_eyebrow}</span>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-2">{c.gallery_title}</h2>
            <p className="mt-3 text-sm text-[var(--srishti-text-secondary)] max-w-xl mx-auto">{c.gallery_subtitle}</p>
          </div>

          {gallery.length === 0 ? (
            <div className="border border-dashed border-[var(--srishti-border)] bg-[var(--srishti-bg)] p-10 text-center">
              <Camera size={28} className="mx-auto text-[var(--srishti-saffron)]" />
              <p className="font-serif-display text-2xl mt-3">Photos coming soon</p>
              <p className="text-sm text-[var(--srishti-text-secondary)] mt-2 max-w-md mx-auto">
                Owners — paste up to four shop photo URLs in <em>Admin → Content</em>.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {gallery.map((g, i) => (
                <div
                  key={i}
                  className={`image-zoom bg-[var(--srishti-bg)] ${
                    i === 0 ? "col-span-2 row-span-2 aspect-square md:aspect-auto" : "aspect-square"
                  }`}
                  data-testid={`gallery-image-${i}`}
                >
                  <img src={g} alt={`Boutique ${i + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-16" data-testid="map-section">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 items-start">
            <div>
              <span className="eyebrow">{c.map_eyebrow}</span>
              <h2 className="font-serif-display text-4xl mt-2 leading-tight">{c.map_title}</h2>
              <p className="mt-4 text-sm text-[var(--srishti-text-secondary)] leading-relaxed">{c.map_paragraph}</p>
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline-gold mt-6 inline-flex"
                data-testid="map-cta-button"
              >
                {c.map_cta}
              </a>
            </div>
            <div className="md:col-span-2 border border-[var(--srishti-border)] bg-[var(--srishti-bg-alt)] aspect-[16/10]">
              <iframe
                title="Shop location"
                src={embedUrl}
                className="w-full h-full"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="google-map-iframe"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-2xl mx-auto px-6 pb-16">
        <MotifDivider label="See you soon" />
      </div>
    </div>
  );
}
